// Migracion.actions.ts

import { urlBackend } from "@/shared/constants";
import { erpApi } from "@/shared/axios";
import axios from "axios";
import {
  CreateTicketsFromCandidatesRequest,
  CreateTicketsFromCandidatesResponse,
  FidelizationConfigSummaryResponse,
  FileUploadResponse,
  MigrationConfigSummaryResponse,
  MigrationParameterRequest,
  ObsoleteCandidateResponse,
  ObsoleteCandidatesFilters,
  PageResponse,
  TicketAreaResponse,
} from "@/erp/campanas/migracion";
import { useAuthStore } from "@/store/auth/auth.store";

const { get, post } = erpApi();

const CAMPANAS_BASE_URL = `${urlBackend}api/v1/campanas`;

export type CampaignType = "MIGRATION" | "FIDELIZATION";
export type DataSourceType = "" | "CSV" | "INTERNAL_SEARCH";

const buildDataSourceParam = (dataSource?: DataSourceType) => {
  return dataSource ? `&dataSource=${encodeURIComponent(dataSource)}` : "";
};

const buildDataSourceQuery = (dataSource?: DataSourceType) => {
  return dataSource ? `?dataSource=${encodeURIComponent(dataSource)}` : "";
};

export const uploadMigrationFile = async (
  file: File,
  campaignType: CampaignType,
) => {
  const formData = new FormData();
  formData.append("file", file);

  return await post(
    `${CAMPANAS_BASE_URL}/file-uploads/${campaignType}`,
    formData,
    true,
  );
};

export const getMigrationUploads = async (
  page = 0,
  size = 10,
  campaignType: CampaignType,
) => {
  return await get<PageResponse<FileUploadResponse>>(
    `${CAMPANAS_BASE_URL}/file-uploads?page=${page}&size=${size}&module=${campaignType}`,
    true,
  );
};

export const downloadErrorsFile = async (id: number) => {
  const storedToken = useAuthStore.getState().token;

  if (!storedToken) {
    throw new Error("No existe token de autenticación");
  }

  return await axios.get(
    `${CAMPANAS_BASE_URL}/file-uploads/${id}/errors/download`,
    {
      responseType: "blob",
      headers: {
        Authorization: "Token " + storedToken,
      },
    },
  );
};

export const createMigrationParameter = async (
  data: MigrationParameterRequest,
) => {
  return await post(`${CAMPANAS_BASE_URL}/migration-parameters`, data, true);
};

export const getMigrationConfigSummary = async () => {
  return await get<MigrationConfigSummaryResponse>(
    `${CAMPANAS_BASE_URL}/config-summary/migration`,
    true,
  );
};

export const getFidelizationConfigSummary = async () => {
  return await get<FidelizationConfigSummaryResponse>(
    `${CAMPANAS_BASE_URL}/config-summary/fidelization`,
    true,
  );
};

export const getObsoleteCandidates = async (
  page = 0,
  size = 10,
  dataSource?: DataSourceType,
) => {
  return await get<PageResponse<ObsoleteCandidateResponse>>(
    `${CAMPANAS_BASE_URL}/service-candidates/obsolete?page=${page}&size=${size}${buildDataSourceParam(
      dataSource,
    )}`,
    true,
  );
};

export const filterObsoleteCandidates = async (
  page = 0,
  size = 10,
  filters: ObsoleteCandidatesFilters,
  dataSource?: DataSourceType,
) => {
  return await post<PageResponse<ObsoleteCandidateResponse>>(
    `${CAMPANAS_BASE_URL}/service-candidates/obsolete/filter?page=${page}&size=${size}${buildDataSourceParam(
      dataSource,
    )}`,
    filters,
    true,
  );
};

export const getTicketAreas = async (page = 1, pageSize = 100) => {
  return await get<PageResponse<TicketAreaResponse>>(
    `${CAMPANAS_BASE_URL}/tickets/areas?page=${page}&pageSize=${pageSize}`,
    true,
  );
};

export const createTicketsFromCandidates = async (
  payload: CreateTicketsFromCandidatesRequest,
) => {
  return await post<CreateTicketsFromCandidatesResponse>(
    `${CAMPANAS_BASE_URL}/tickets/from-candidates`,
    payload,
    true,
  );
};

export interface NotFoundCandidateResponse {
  candidateId: number;
  identification: string;
  customerName: string;
  planName: string;
  zoneName: string;
  equipmentName: string;
  installationDate: string;
  reason: string;
}

export const getNotFoundCandidates = async (
  module: "MIGRATION" | "FIDELIZATION",
  dataSource?: DataSourceType,
) => {
  return await get<NotFoundCandidateResponse[]>(
    `${CAMPANAS_BASE_URL}/service-candidates/not-found/${module}${buildDataSourceQuery(
      dataSource,
    )}`,
    true,
  );
};

export interface InternalSearchCandidateRequest {
  module: CampaignType;
  desde?: string;
  hasta?: string;
  filters?: {
    fecha_desde: string;
    plan_nombre?: string;
    estado_contrato?: string;
    formas_pago_excluidas?: string[];
    ciudad?: string;
    tipo_plan?: string;
    departamento?: string;
  };
}

export interface InternalSearchCandidateResponse {
  module: CampaignType;
  totalContractsFound: number;
  savedCandidates: number;
  ignoredWithoutDate: number;
  message: string;
}

export interface ContratoCampanasMigracionFiltroRequest {
  ciudad?: string;
  tipo_plan?: string;
  tipo_servicio?: string;
  estados_contrato?: string[];
  formas_pago_excluidas?: string[];
  observacion_instalado?: string;
  observacion_excluida?: string;
  modelos?: string[];
}

export interface MigrationContractOptionsResponse {
  observaciones: string[];
  modelos: string[];
  ciudades: string[];
  tiposPlan: string[];
  tiposServicio: string[];
  estadosContrato: string[];
  formasPago: string[];
}

export interface ContratoCampanasMigracionResponse {
  candidateId: number;
  idContrato: string;
  nombreCliente: string;
  estadoContrato: string;
  modelo: string;
  fechaEquipoAntiguo: string;
  tickets: number;
  ciudad: string;
}

export interface SendMigrationReportEmailRequest {
  to: string[];
  subject: string;
  message: string;
  module: "MIGRATION";
  migrationFilters: ContratoCampanasMigracionFiltroRequest;
}

export interface MigrationEmailResponse {
  success: boolean;
  message: string;
  totalRecipients: number;
}

export const searchInternalCandidates = async (
  payload: InternalSearchCandidateRequest,
) => {
  return await post<InternalSearchCandidateResponse>(
    `${CAMPANAS_BASE_URL}/service-candidates/internal-search`,
    payload,
    true,
  );
};

export const searchMigrationContracts = async (
  payload: ContratoCampanasMigracionFiltroRequest,
) => {
  return await post<ContratoCampanasMigracionResponse[]>(
    `${CAMPANAS_BASE_URL}/tickets/contracts/migration/search`,
    payload,
    true,
  );
};

export const getMigrationContractOptions = async () => {
  return await get<MigrationContractOptionsResponse>(
    `${CAMPANAS_BASE_URL}/tickets/contracts/migration/options`,
    true,
  );
};

export const sendMigrationCampaignReportEmail = async (
  payload: SendMigrationReportEmailRequest,
) => {
  return await post<MigrationEmailResponse>(
    `${CAMPANAS_BASE_URL}/emails/send-report`,
    payload,
    true,
  );
};

export interface SendCampaignEmailWithAttachmentsRequest {
  to: string[];
  subject: string;
  message: string;
  files?: File[];
}

export const sendCampaignEmailWithAttachments = async ({
  to,
  subject,
  message,
  files = [],
}: SendCampaignEmailWithAttachmentsRequest) => {
  const storedToken = useAuthStore.getState().token;

  if (!storedToken) {
    throw new Error("No existe token de autenticación");
  }

  const formData = new FormData();

  to.forEach((email) => {
    formData.append("to", email);
  });

  formData.append("subject", subject);
  formData.append("message", message);

  files.forEach((file) => {
    formData.append("files", file);
  });

  return await axios.post(
    `${CAMPANAS_BASE_URL}/emails/send-with-attachments`,
    formData,
    {
      headers: {
        Authorization: "Token " + storedToken,
      },
    },
  );
};
